import { space, SpaceBetween, tok } from '@sunwebgroup/ui-components'

import { Button, Icon, Sheet, Typography } from '@sunwebgroup/ui-components/uds'

import { isEmpty } from 'ramda'
import { use, useState } from 'react'
import { styled } from 'styled-components'
import { useIntl } from '@/intl'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { busesSelector } from '@/scopes/prices/components/selectors'
import { BusCard } from '@/scopes/prices/components/TransportSelector/BusSelector/BusCard'
import { InboundTitle, OutboundTitle } from '@/scopes/prices/components/TransportSelector/BusSelector/Headers'
import { createBusComponentInteractionEvent } from '@/scopes/prices/domain/analytics'
import { PricesActionsContext } from '@/scopes/prices/domain/PricesActionsContext'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'

const Section = styled(SpaceBetween)`
  outline: none;
  flex-basis: 100%;
  margin-bottom: ${space('primitives_space_16')};
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const SheetCustomHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 48px;
  height: 48px;
  box-sizing: border-box;
  padding: 0 ${space('primitives_space_16')};
  border-bottom: 2px solid ${tok('semantics_dividers_color')};
  background-color: ${tok('primitives_color_neutral_0')};
  position: relative;
`
const ReturnWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`
const STEPS = {
  selectDeparture: 'selectDeparture',
  selectReturn: 'selectReturn',
}

const BusSelectorTitle = styled(Typography)`
  display: block;
`
const BusSelectorContainer = styled.div`
  border: solid ${tok('primitives_color_neutral_100')};
  border-width: 2px 0 0 0;
  padding: ${space('primitives_space_16')};
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_16')};
`

const SheetTitle = styled(Typography)`
  padding-top: ${space('primitives_space_20')};
`

const BusCardWrapper = styled.div`
  padding-top: ${space('primitives_space_16')};
`

const HeaderCell = styled.div<{ alignment?: 'flex-start' | 'center' | 'flex-end' }>`
  display: flex;
  flex-basis: 33%;
  justify-content: ${({ alignment }) => alignment || 'flex-start'};
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${space('primitives_space_16')} ${space('primitives_space_32')} ${space('primitives_space_16')} 0;
  bottom: 0;
  position: fixed;
  background-color: ${tok('primitives_color_neutral_0')};
  width: 100%;
  border-top: 2px solid ${tok('semantics_dividers_color')};
`
const SheetContent = styled(Sheet.Content)`
  padding-bottom: ${space('primitives_space_96')} !important;
`

export function BusSelectorMobile() {
  const intl = useIntl()
  const {
    setOutboundBusId,
    setInboundBusId,
  } = use(PricesActionsContext)

  const { outboundBuses, inboundBuses, selectedOutboundBusId, selectedInboundBusId, outboundBus, inboundBus }
    = usePricesSelector(busesSelector)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [step, setStep] = useState<string | null>(null)
  const { sendEvent } = useAnalytics()
  const sheetTitle
    = step === STEPS.selectDeparture
      ? `${intl.formatMessage({ id: 'prices_transport_selector_bus-selector_select_your_bus_outward' })}`
      : `${intl.formatMessage({ id: 'prices_transport_selector_bus-selector_select_your_bus_inward' })}`
  if (isEmpty(outboundBuses) || isEmpty(inboundBuses))
    return null

  const handleOpenModal = () => {
    setIsModalOpen(true)
    setStep(STEPS.selectDeparture)
    sendAnalyticsEvent('outbound')
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setStep(null)
  }

  function changeStep(text: string) {
    setStep(text)
    sendAnalyticsEvent(text === STEPS.selectDeparture ? 'outbound' : 'inbound')
  }

  function sendAnalyticsEvent(type: string) {
    sendEvent(createBusComponentInteractionEvent({ type, result: 'Change Bus' }))
  }

  const selectOutboundBus = (id: string) => {
    setOutboundBusId(id)
  }

  const selectInboundBus = (id: string) => {
    setInboundBusId(id)
  }

  return (
    <>
      <BusSelectorContainer>
        <BusSelectorTitle variant="display-f-regular">
          {intl.formatMessage({ id: 'prices_transport_selector_bus-selector_title' })}
        </BusSelectorTitle>
        <Wrapper id="BusSelector">
          <Section direction="y">
            <OutboundTitle />
            {outboundBus && <BusCard {...outboundBus} isSelected noActiveState stationName={outboundBus.departureStationName} cityName={outboundBus.departureCityName} date={outboundBus.departureDate} />}
          </Section>
          <Section direction="y">
            <InboundTitle />
            {inboundBus && <BusCard {...inboundBus} isSelected noActiveState stationName={inboundBus.arrivalStationName} cityName={inboundBus.arrivalCityName} />}
          </Section>
          <Button variant="secondary" onClick={handleOpenModal}>
            {intl.formatMessage({
              id: 'prices_transport_selector_bus-selector-mobile_button',
            })}
          </Button>
        </Wrapper>
        <Sheet.Root open={isModalOpen} onOpenChange={handleCloseModal}>
          <Sheet.Body side="right">
            <SheetCustomHeader>
              <HeaderCell>
                {step === STEPS.selectReturn && (
                  <ReturnWrapper onClick={() => changeStep(STEPS.selectDeparture)}>
                    <Icon glyph="ChevronLeft" size={20}></Icon>
                    <Typography variant="body-m-bold-tight">
                      {intl.formatMessage({ id: 'prices_transport_selector_bus-selector_outward' })}
                    </Typography>
                  </ReturnWrapper>
                )}
              </HeaderCell>
              <HeaderCell alignment="center">
                <Typography variant="body-m-bold-tight">
                  {step === STEPS.selectDeparture
                    ? intl.formatMessage({ id: 'prices_transport_selector_bus-selector_outward-bus' })
                    : intl.formatMessage({ id: 'prices_transport_selector_bus-selector-inward-bus' })}
                </Typography>
              </HeaderCell>
              <HeaderCell alignment="flex-end">
                <Button variant="transparentPrimary" onClick={handleCloseModal}>
                  {intl.formatMessage({ id: 'prices_transport_selector_bus-selector_cancel' })}
                </Button>
              </HeaderCell>
            </SheetCustomHeader>
            <SheetContent>
              <SheetTitle variant="body-m-regular-snug">{sheetTitle}</SheetTitle>
              {step === STEPS.selectDeparture && (
                <div>
                  {outboundBuses.map(bus => (
                    <BusCardWrapper key={bus.id}>
                      <BusCard
                        {...bus}
                        cityName={bus.departureCityName}
                        stationName={bus.departureStationName}
                        date={bus.departureDate}
                        setSelected={selectOutboundBus}
                        isSelected={selectedOutboundBusId === bus.id}
                      />
                    </BusCardWrapper>
                  ))}
                </div>
              )}
              {step === STEPS.selectReturn && (
                <div>
                  {inboundBuses.map(bus => (
                    <BusCardWrapper key={bus.id}>
                      <BusCard
                        {...bus}
                        cityName={bus.arrivalCityName}
                        stationName={bus.arrivalStationName}
                        setSelected={selectInboundBus}
                        isSelected={selectedInboundBusId === bus.id}
                      />
                    </BusCardWrapper>
                  ))}
                </div>
              )}
              <ButtonWrapper>
                {step === STEPS.selectDeparture && (
                  <Button onClick={() => changeStep(STEPS.selectReturn)} fullWidth>
                    {intl.formatMessage({ id: 'prices_transport_selector_bus-selector_save_and_continue' })}
                  </Button>
                )}
                {step === STEPS.selectReturn && (
                  <Button onClick={handleCloseModal} fullWidth>
                    {intl.formatMessage({ id: 'prices_transport_selector_bus-selector-save' })}
                  </Button>
                )}
              </ButtonWrapper>
            </SheetContent>
          </Sheet.Body>
        </Sheet.Root>
      </BusSelectorContainer>
    </>
  )
}
